import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, C360CardGrid, ColumnChooser, ViewSwitch, DEFAULT_VIEW_MODES, KpiCard, DonutCard,
  ViewIcon, EditIcon, DeleteIcon, CardIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  fmtMoney, fmtDate, parseTiers, PERIOD_TYPES, METRIC_TYPES, CUSTOMER_TARGET_TYPES,
} from './target/targetUtils';

// /api/targets REST controller'ı (OData değil) ApiResponse<List<Target>> döner →
// c360 UI kabuğu + yerel filtre/sıralama/sayfalama (Kampanya ekranıyla aynı desen).
const API_BASE = '/api/targets';
const PAGE_SIZE_DEFAULT = 10;
const FETCH_TAKE = 500;

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];
const PERIOD_OPTIONS = PERIOD_TYPES.map((p) => ({ value: p.value, label: p.label }));
const METRIC_OPTIONS = METRIC_TYPES.map((m) => ({ value: m.value, label: m.label }));

const PERIOD_LABEL = Object.fromEntries(PERIOD_TYPES.map((p) => [p.value, p.label]));
const METRIC_LABEL = Object.fromEntries(METRIC_TYPES.map((m) => [m.value, m.label]));
const CUSTOMER_LABEL = Object.fromEntries(CUSTOMER_TARGET_TYPES.map((c) => [c.value, c.label]));

const COLUMN_FILTER_META = {
  code: { type: 'text', odataField: 'Code' },
  name: { type: 'text', odataField: 'Name' },
  periodType: { type: 'select', odataField: 'PeriodType', options: PERIOD_OPTIONS },
  metricType: { type: 'select', odataField: 'MetricType', options: METRIC_OPTIONS },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: ACTIVE_OPTIONS },
};
const SEARCH_FIELDS = ['Code', 'Name', 'Description'];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapList(payload) {
  const data = pick(payload, 'data', 'Data');
  if (Array.isArray(data)) return data;
  // savunmacı: {items} sarmalı da gelebilir
  const items = pick(data, 'items', 'Items');
  return Array.isArray(items) ? items : [];
}

function matchColumnFilter(meta, filter, rawValue) {
  if (!meta || !filter) return true;
  if (meta.type === 'text') {
    const needle = String(filter.value ?? '').trim().toLocaleLowerCase('tr-TR');
    if (!needle) return true;
    return String(rawValue ?? '').toLocaleLowerCase('tr-TR').includes(needle);
  }
  if (meta.type === 'select') {
    const target = filter.value;
    if (target === '' || target == null) return true;
    if (meta.valueFormat === 'raw') return String(!!rawValue) === String(target);
    return String(rawValue) === String(target);
  }
  return true;
}

// Liste + Kart + Grafik (kanban yok). Kart, Liste'nin hemen YANINDA (index 1).
const TARGET_VIEW_MODES = (() => {
  const base = DEFAULT_VIEW_MODES.filter((m) => m.value !== 'kanban');
  const list = base.find((m) => m.value === 'list');
  const rest = base.filter((m) => m.value !== 'list');
  const cardMode = { value: 'card', label: 'Kart', icon: CardIcon };
  return list ? [list, cardMode, ...rest] : [cardMode, ...base];
})();

function metricText(row) {
  const val = fmtMoney(row.targetValue);
  return row.metricType === 'AMOUNT' ? `${val} ₺` : `${val} ${row.unit || ''}`.trim();
}

function audienceText(row) {
  return CUSTOMER_LABEL[row.customerTargetType] || 'Tüm Müşteriler';
}

// Grafik görünümü — diğer c360 liste ekranlarındaki ChartView deseni.
function TargetChartView({ rows }) {
  const total = rows.length;
  const active = rows.filter((r) => r.isActive).length;
  const passive = total - active;
  const amountBased = rows.filter((r) => r.metricType === 'AMOUNT').length;
  const qtyBased = total - amountBased;
  const now = new Date();
  const ymCurrent = now.getFullYear() * 100 + (now.getMonth() + 1);
  const newThisMonth = rows.filter((r) => {
    if (!r.createdDate) return false;
    const d = new Date(r.createdDate);
    if (Number.isNaN(d.getTime())) return false;
    return d.getFullYear() * 100 + (d.getMonth() + 1) === ymCurrent;
  }).length;

  const periodCounts = {};
  rows.forEach((r) => { const k = PERIOD_LABEL[r.periodType] || r.periodType || '—'; periodCounts[k] = (periodCounts[k] || 0) + 1; });
  const periodData = Object.entries(periodCounts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

  const statusData = [
    { name: 'Aktif', value: active, color: '#00c875' },
    { name: 'Pasif', value: passive, color: '#94a3b8' },
  ].filter((d) => d.value > 0);

  const metricDonut = [
    { name: 'TL / Ciro', value: amountBased, color: '#2563eb' },
    { name: 'Ürün Birimi', value: qtyBased, color: '#7c3aed' },
  ].filter((d) => d.value > 0);

  return (
    <div className="c360-dashboard">
      <div className="c360-kpi-grid">
        <KpiCard label="Toplam Hedef" value={total.toLocaleString('tr-TR')} />
        <KpiCard label="Aktif" value={active.toLocaleString('tr-TR')} hint={total > 0 ? `%${((active / total) * 100).toFixed(0)} aktif` : '—'} />
        <KpiCard label="Pasif" value={passive.toLocaleString('tr-TR')} />
        <KpiCard label="TL / Ciro" value={amountBased.toLocaleString('tr-TR')} hint="tutar bazlı" />
        <KpiCard label="Ürün Birimi" value={qtyBased.toLocaleString('tr-TR')} hint="miktar bazlı" />
        <KpiCard label="Bu Ay Eklenen" value={newThisMonth.toLocaleString('tr-TR')} hint="yeni hedef" />
      </div>

      <div className="c360-chart-panel">
        <div className="c360-chart-panel-head">
          <span className="c360-chart-panel-title">Döneme Göre Dağılım</span>
          <span className="c360-chart-panel-sub">{periodData.length} dönem tipi</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={periodData} margin={{ top: 12, right: 8, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" fontSize={11} interval={0} height={40} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip cursor={{ fill: 'rgba(37, 99, 235, 0.06)' }} formatter={(v) => [`${v} hedef`, 'Adet']} />
            <Bar dataKey="value" fill="#2563eb" radius={[8, 8, 0, 0]}>
              <LabelList dataKey="value" position="top" style={{ fill: '#334155', fontWeight: 800, fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="c360-chart-donuts">
        <DonutCard title="Durum Dağılımı" subtitle={`${total} hedef`} data={statusData} />
        <DonutCard title="Metrik Dağılımı" subtitle={`${total} hedef`} data={metricDonut} />
      </div>
    </div>
  );
}

export function TargetsPage() {
  usePageMeta('Hedef & Teşvik', 'Çok boyutlu hedef ve kademeli prim kuralları.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  const [viewMode, setViewMode] = useState('list');

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true); // ilk paint'te boş-durum yanıp sönmesin
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}?take=${FETCH_TAKE}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const items = unwrapList(payload);
      const mapped = items.map((r) => ({
        id: pick(r, 'id', 'Id'),
        code: pick(r, 'code', 'Code') ?? '',
        name: pick(r, 'name', 'Name') ?? '',
        description: pick(r, 'description', 'Description') ?? '',
        periodType: pick(r, 'periodType', 'PeriodType') ?? 'MONTH',
        startDate: pick(r, 'startDate', 'StartDate') ?? null,
        endDate: pick(r, 'endDate', 'EndDate') ?? null,
        metricType: pick(r, 'metricType', 'MetricType') ?? 'AMOUNT',
        unit: pick(r, 'unit', 'Unit') ?? '',
        targetValue: pick(r, 'targetValue', 'TargetValue') ?? 0,
        customerTargetType: pick(r, 'customerTargetType', 'CustomerTargetType') ?? 'ALL',
        tiersJson: pick(r, 'tiersJson', 'TiersJson') ?? '',
        isActive: pick(r, 'isActive', 'IsActive') ?? false,
        createdBy: pick(r, 'createdBy', 'CreatedBy') ?? '',
        createdDate: pick(r, 'createdDate', 'CreatedDate') ?? null,
        updatedDate: pick(r, 'updatedDate', 'UpdatedDate') ?? null,
      }));
      setAllRows(mapped);
      setCurrentPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const searchKeys = SEARCH_FIELDS.map((f) => f.charAt(0).toLowerCase() + f.slice(1));
    const matches = (row) => {
      if (needle) {
        const hit = searchKeys.some((k) => String(row[k] ?? '').toLocaleLowerCase('tr-TR').includes(needle));
        if (!hit) return false;
      }
      for (const [field, filter] of Object.entries(columnFilters)) {
        if (!matchColumnFilter(COLUMN_FILTER_META[field], filter, row[field])) return false;
      }
      return true;
    };
    const filtered = allRows.filter(matches);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Code': return String(row.code ?? '');
        case 'Name': return String(row.name ?? '');
        case 'TargetValue': return Number(row.targetValue ?? 0);
        case 'StartDate': return row.startDate ? new Date(row.startDate).getTime() : 0;
        case 'UpdatedDate':
        default: return row.updatedDate ? new Date(row.updatedDate).getTime() : 0;
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a); const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const rows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const applyFilterText = (text) => { setFilterText(text); setCurrentPage(1); };
  const setColumnFilter = (columnId, next) => { setColumnFilters((p) => ({ ...p, [columnId]: next })); setCurrentPage(1); };
  const clearColumnFilter = (columnId) => {
    setColumnFilters((p) => { const c = { ...p }; delete c[columnId]; return c; });
    setCurrentPage(1);
  };
  const applySort = (odataField, dir) => { setSortField(odataField || 'UpdatedDate'); setSortDir(dir || 'asc'); setCurrentPage(1); };

  const openNew = () => navigate('/targets/new');
  const openDetail = (row) => { if (row?.id) navigate(`/targets/${row.id}`); };

  const handleDelete = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/${selected.id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Hedef silindi.');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
      setDeleteConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Hedef', meta: { filterType: 'text' },
      cell: ({ row }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>🎯</span>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0 }}>
            <span style={{ fontWeight: 700 }}>{row.original.name || '-'}</span>
            <span style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.original.code}{row.original.description ? ` · ${row.original.description}` : ''}
            </span>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor('periodType', {
      header: 'Dönem', meta: { filterType: 'select' },
      cell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, lineHeight: 1.3 }}>
          <StatusBadge tone="neutral">{PERIOD_LABEL[row.original.periodType] || row.original.periodType}</StatusBadge>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-secondary, #6b7280)', whiteSpace: 'nowrap' }}>
            <span aria-hidden="true">🗓</span>{fmtDate(row.original.startDate)} – {fmtDate(row.original.endDate)}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor('metricType', {
      header: 'Metrik & Hedef', meta: { filterType: 'select' },
      cell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, lineHeight: 1.3 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>{metricText(row.original)}</span>
          <span style={{ fontSize: 11, color: 'var(--text-info, #185fa5)' }}>{METRIC_LABEL[row.original.metricType] || row.original.metricType}</span>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'audience', header: 'Kitle',
      cell: ({ row }) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
          <span aria-hidden="true">👥</span>{audienceText(row.original)}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'tiers', header: 'Kademe', enableColumnFilter: false,
      cell: ({ row }) => {
        const tiers = parseTiers(row.original.tiersJson);
        if (!tiers.length) return <span style={{ fontSize: 12, color: 'var(--text-secondary, #9ca3af)' }}>—</span>;
        return <StatusBadge tone="wait">{tiers.length} kademe</StatusBadge>;
      },
    }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="neutral">Pasif</StatusBadge>),
    }),
    columnHelper.display({
      id: 'created', header: 'Oluşturma',
      cell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, fontSize: 12 }}>
          <span>{fmtDate(row.original.createdDate)}</span>
          <span style={{ fontSize: 10, color: 'var(--text-secondary, #6b7280)' }}>{row.original.createdBy || '-'}</span>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Düzenle" aria-label="Düzenle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('UpdatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = () => {
    try {
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const cols = [
        { field: 'code', headerName: 'Kod' },
        { field: 'name', headerName: 'Ad' },
        { headerName: 'Dönem', valueGetter: ({ row }) => PERIOD_LABEL[row.periodType] || row.periodType },
        { headerName: 'Başlangıç', valueGetter: ({ row }) => fmtDate(row.startDate) },
        { headerName: 'Bitiş', valueGetter: ({ row }) => fmtDate(row.endDate) },
        { headerName: 'Metrik', valueGetter: ({ row }) => METRIC_LABEL[row.metricType] || row.metricType },
        { headerName: 'Hedef', valueGetter: ({ row }) => metricText(row) },
        { headerName: 'Kademe', valueGetter: ({ row }) => parseTiers(row.tiersJson).length },
        { headerName: 'Durum', valueGetter: ({ row }) => (row.isActive ? 'Aktif' : 'Pasif') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `hedefler_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod / ad / açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Hedef & Teşvik"
        subtitle="Sahayı motive eden çok boyutlu hedef ve kademeli prim kurallarını oluşturun, filtreleyin ve yönetin."
        actions={(
          <>
            <ViewSwitch value={viewMode} onChange={setViewMode} modes={TARGET_VIEW_MODES} />
            {canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Ekle</button>}
          </>
        )}
        toolbar={toolbarNode}
        footer={viewMode === 'chart' ? null : footerNode}
      >
        {viewMode === 'chart' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <TargetChartView rows={filteredSorted} />
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'card' ? (
          <C360CardGrid
            rows={table.getRowModel().rows.map((r) => r.original)}
            card={(row) => ({
              avatarName: row.name,
              avatarTone: 'rep',
              title: row.name,
              subtitle: row.code,
              statusTone: row.isActive ? 'ok' : 'neutral',
              statusLabel: row.isActive ? 'Aktif' : 'Pasif',
              meta: [
                { k: 'Dönem', v: PERIOD_LABEL[row.periodType] || row.periodType },
                { k: 'Hedef', v: metricText(row) },
                { k: 'Kitle', v: audienceText(row) },
              ].filter(Boolean),
            })}
            actions={(row) => [
              { kind: 'view', title: 'Görüntüle', onClick: openDetail },
              { kind: 'edit', title: 'Düzenle', onClick: openDetail, hidden: !canEdit },
              { kind: 'delete', title: 'Sil', onClick: (r) => { setSelected(r); setDeleteConfirm(true); }, hidden: !canDelete },
            ]}
            onCardClick={setSelected}
            onCardDoubleClick={openDetail}
            selectedId={selected?.id}
            loading={loading}
          />
        ) : (
          <C360Table
            table={table}
            columnFilterMeta={COLUMN_FILTER_META}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={openDetail}
            emptyText="Kayıt bulunamadı"
          />
        )}
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected.name}"</strong> hedefini silmek istediğinize emin misiniz?</p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#dc2626' }}>Bu hedefe bağlı prim hesapları artık üretilmeyecek.</p>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btnDanger, opacity: saving ? 0.6 : 1 }} onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor...' : 'Evet, Sil'}</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default TargetsPage;
